import { subTypes } from "../../Editor/itemTypes";
import getQuestion from "../../Editor/getQuestion";
import i18n from "../../i18n";
import moment from "moment";
import { getLabelId, getLabelProps } from "../../Analyser/moodboardModel";

const getAnswerValue = ({ answer, question, answers }) => {
  let primaryScore = "";
  let score = "";
  let scoreArr = [];
  switch (question.typeQuestion) {
    case subTypes.open:
      scoreArr = answer.sAnswer;
      primaryScore = answer.sAnswer[0];
      score = primaryScore;
      return { scoreArr, primaryScore, score, item: question, answer };

    case subTypes.yesno:
      const yesNoOptions = getQuestion(subTypes.yesno).options.map(
        (option) => option.title
      );
      primaryScore = answer.iAnswer.map((index) => index + 1);
      scoreArr = primaryScore.map((iAnswerIndex) =>
        yesNoOptions.at(iAnswerIndex - 1)
      );
      score = [...scoreArr].join(", ");
      return {
        scoreArr,
        primaryScore,
        score,
        options: yesNoOptions,
        item: question,
        answer,
      };

    case subTypes.count:
      const pressed = i18n.t("editor.questionTypes.count.placeholder");
      primaryScore = pressed;
      score = pressed;
      scoreArr = [pressed];
      return { scoreArr, primaryScore, score, item: question, answer };

    case subTypes.order:
    case subTypes.imageChoice:
    case subTypes.multipleChoice:
      let options = question?.itemExtra?.options;
      if (answer.sAnswer != null) {
        if (Array.isArray(answer.sAnswer)) {
          options = answer.sAnswer;
        } else {
          //legacy
          if (Array.isArray(question?.itemExtra?.otherOption)) {
            options[question.itemExtra.otherOption[0] - 1] = answer.sAnswer;
          } else {
            options[question.itemExtra.otherOption - 1] = answer.sAnswer;
          }
        }
      }
      primaryScore = answer.iAnswer;
      scoreArr = answer.iAnswer.map((iAnswerIndex) =>
        options.at(iAnswerIndex - 1)
      );
      score = [...scoreArr].join(", ");
      return { primaryScore, score, options, scoreArr, item: question, answer };

    case subTypes.likert:
      scoreArr = answer.iAnswer;
      primaryScore = answer.iAnswer;
      score = answer.iAnswer.join(", ");
      return { primaryScore, score, scoreArr, item: question, answer };

    case subTypes.bodyParts:
    case subTypes.multiSmiley:
      primaryScore = [];
      score = [];
      const specialOptions = getQuestion(question.typeQuestion).options;
      const optionTypes = new Set([
        ...answer.sAnswer,
        ...specialOptions.map(({ type }) => type),
      ]);
      const optionTitles = Array.from(optionTypes).map(
        (optionType, optionIndex) => {
          let optionTitle = optionType; // default option title is the type of option
          const option = specialOptions.find(({ type }) => type === optionType); // find the translation of the option
          if (option) optionTitle = option.title; // translation is available, so use the translation

          if (answer.sAnswer.includes(optionType)) {
            primaryScore.push(optionIndex + 1);
            score.push(optionTitle);
          }

          return optionTitle;
        }
      );
      return {
        scoreArr: score,
        primaryScore,
        score: score.join(", "),
        options: optionTitles,
        item: question,
        answer,
      };

    case subTypes.smiley:
    case subTypes.sliderNegPos:
    case subTypes.sliderNeutralPos:
      const minInt = question.itemExtra?.minInt ?? 0;
      const maxInt = question.itemExtra?.maxInt ?? 100;
      primaryScore = answer.iAnswer[0];
      score = Math.round(((primaryScore - minInt) / (maxInt - minInt)) * 100);
      scoreArr = [score];
      return { primaryScore, score, scoreArr, item: question, answer };

    case subTypes.computation:
      let computation = { scoreArr, primaryScore, score };
      let minVis = question.itemExtra?.minVis ?? 0;
      let maxVis = question.itemExtra?.maxVis ?? 100;
      if (question.itemExtra?.JSON?.length > 0) {
        const item = { ...answer, itemExtra: answer.basicQuestion };
        const findBdItem = answers.find(
          (systemAnswer) =>
            systemAnswer?.basicQuestion?.shortQuestion === "system_dateOfBirth"
        );
        const bdItem = findBdItem
          ? { ...findBdItem, itemExtra: findBdItem.itemExtra }
          : null;
        const findGenderItem = answers.find(
          (systemAnswer) =>
            systemAnswer?.basicQuestion?.shortQuestion === "system_gender"
        );
        const genderItem = findGenderItem
          ? {
              ...findGenderItem,
              itemExtra: findGenderItem.basicQuestion,
            }
          : null;
        const statements = window.getNormingResults({
          item,
          bdItem,
          genderItem,
          lang: i18n.language,
        }).statements;
        computation.norming = statements;
      }
      if (minVis === 0 && maxVis === 0) {
        minVis = 0;
        maxVis = 100;
      } // null is translated to 0 in basicQuestion
      computation.scoreArr = answer?.dAnswer;
      computation.primaryScore = Number(answer?.dAnswer?.[0].toFixed(2));
      computation.score = Math.round(
        ((computation.primaryScore - minVis) / (maxVis - minVis)) * 100
      );
      computation.item = question;
      computation.answer = answer;
      return computation;

    case subTypes.time:
      //scoreArr = answer.iAnswer;
      //primaryScore = answer.iAnswer[0];
      primaryScore = moment.utc(answer.iAnswer[0] * 1000).format("HH:mm");
      scoreArr = [primaryScore];
      score = Math.round((answer.iAnswer[0] / (3600 * 24)) * 100);
      return { primaryScore, score, scoreArr, item: question, answer };

    default:
      return { primaryScore, score, scoreArr, item: question, answer };
  }
};

// 3 lagacy answer-cases: itemPos(new), basicquestion (older), nothing (oldest)
const importQuestion = (answer, survey) => {
  if ("basicQuestion" in answer) {
    return {
      shortQuestion: answer.basicQuestion.shortQuestion,
      fullQuestion: answer.basicQuestion.fullQuestion,
      typeQuestion: answer.basicQuestion.typeQuestion,
      visPri: answer.basicQuestion?.visPri ?? 0,
      itemExtra: answer.basicQuestion,
    };
  }
  if ("itemPos" in answer && answer.itemPos !== null && answer.itemPos !== "") {
    let indexArr =
      answer?.itemPos?.split(".").map((index) => Number(index)) ?? []; // position index of the answer
    indexArr.shift(); // remove first index (always 0)
    // returns the question of the survey
    const getQuestionByIndex = (questions, indexArr) => {
      const index = indexArr.shift();
      if (indexArr.length > 0) {
        return getQuestionByIndex(questions[index].items, indexArr);
      }
      return { ...questions[index] };
    };
    const question = getQuestionByIndex(survey.items, indexArr); // get the question
    return { ...question, visPri: question?.itemExtra?.visPri ?? 0 };
  }
  return {
    shortQuestion: answer.shortQuestion,
    fullQuestion: answer.fullQuestion,
    typeQuestion: answer.typeQuestion,
    visPri: answer?.itemExtra?.visPri ?? 0,
    itemExtra: answer?.itemExtra,
  };
};

export default function importAnswer({ beep, interactions }) {
  const answers = beep.data.answers;
  const survey = interactions[beep.questionListLink];

  let results = []; // all results
  let importantLabelProps = [];

  answers.forEach((answer) => {
    const q = importQuestion(answer, survey); // get the question (3 lagacy cases)
    const fullQuestion = new DOMParser().parseFromString(
      q?.fullQuestion ?? "",
      "text/html"
    ).body.textContent; // filter out html tags in fullQuestion
    const question = { ...q, fullQuestion };
    const answerValue = getAnswerValue({ answer, question, answers }); // get the answer
    if (question.typeQuestion && question.typeQuestion !== "basic") {
      const label = question.shortQuestion;
      const type = question.typeQuestion;
      const index = [...results].filter(
        ({ labelProps }) =>
          labelProps.label === label && labelProps.type === type
      ).length;
      const result = {
        ...answerValue,
        id: getLabelId({ label, type, index }), // system label
        label, // presetation label
        type,
        number: results.length + 1, // order number
        labelProps: getLabelProps({
          label,
          type,
          index,
        }),
        isQuantitative: [
          "time",
          "computation",
          "smiley",
          "sliderNegPos",
          "sliderNeutralPos",
        ].some((quantitative) => type === quantitative),
        question: question?.fullQuestion ?? "", // question title
        visPri: question.visPri, // visualization importance
      };
      if (result.visPri > -100) {
        // Display all visible items in a table
        results.push(result);

        // likert question (convert multiple choice answer to slider)
        if (
          question?.itemExtra?.optionVals?.length > 0 &&
          answer?.iAnswer?.length === 1
        ) {
          const optionVals = question.itemExtra.optionVals;
          const minInt = Math.min(...optionVals);
          const maxInt = Math.max(...optionVals);
          const iAnswerIndex = answer.iAnswer[0];
          const optionVal = optionVals.at(iAnswerIndex - 1);
          const score = {
            primaryScore: optionVal,
            score: Math.round(((optionVal - minInt) / (maxInt - minInt)) * 100),
          };
          const likertLabel = `${result.label}`;
          const likertType = "sliderNegPos";
          const likertIndex = [...results].filter(
            ({ labelProps }) =>
              labelProps.label === likertLabel && labelProps.type === likertType
          ).length;
          const likertId = getLabelId({
            label: likertLabel,
            type: likertType,
            index: likertIndex,
          });
          const likertAnswer = {
            ...result,
            ...score,
            id: likertId,
            label: likertLabel,
            type: likertType,
            number: `${result.number}'`,
            labelProps: getLabelProps({
              label: likertLabel,
              type: likertType,
              subType: "likert",
              index: likertIndex,
            }),
            isQuantitative: true,
          };
          results.push(likertAnswer);
        }
      }
    }
  });

  [...results]
    .sort((a, b) => b.visPri - a.visPri)
    .forEach((result) => {
      // show results of vispri > 100
      if (result.visPri > 100) {
        importantLabelProps.push(result.labelProps);
      }
    });
  // if no labels have a vispri > 100, select the first 3 ordened by vispri
  if (importantLabelProps.length === 0) {
    const quantitativeLabels = results
      .sort((a, b) => b.visPri - a.visPri)
      .filter(({ isQuantitative }) => isQuantitative)
      .splice(0, 3);
    const qualitativeLabels = results
      .sort((a, b) => b.visPri - a.visPri)
      .filter(({ isQuantitative }) => !isQuantitative)
      .splice(0, 3);
    importantLabelProps = [
      ...quantitativeLabels.map(({ labelProps }) => labelProps),
      ...qualitativeLabels.map(({ labelProps }) => labelProps),
    ];
  }

  return {
    id: beep.id,
    beepId: beep.data.beepId,
    title: beep.data.name,
    time: beep.timeStampStart,
    endTime: beep.timeStampEnd,
    questionList: interactions[beep.questionListLink],
    about: survey?.itemExtra,
    importantLabelProps,
    table: results,
  };
}
